import { useState, useCallback, useEffect } from 'react';
import { HeartPulse, RefreshCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { useOuraHeartRate } from '../../../../hooks/devices/oura-ring/useOuraHeartRate';
import { useSessionsByDate } from '../../../../hooks/sessions/useSessionsByDate';
import { useSessionPpg } from '../../../../hooks/sessions/useSessionPpg';
import { LoadingState, ErrorState } from './states';
import { PopupContent } from './ui';
import { calculateZoomDomain } from './utils';
import { DatePicker } from './DatePicker';
import { BasePopup } from '../../BasePopup';

interface OuraHeartRatePopupProps {
  onClose: () => void;
  sessionId?: string;
}

export function OuraHeartRatePopup({
  onClose,
  sessionId: initialSessionId,
}: OuraHeartRatePopupProps) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(initialSessionId);
  const [showSessionDetails, setShowSessionDetails] = useState<boolean>(!!initialSessionId);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [zoomDomain, setZoomDomain] = useState<{
    xStart: Date;
    xEnd: Date;
  } | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showSession, setShowSession] = useState(true);

  const {
    heartRateData,
    isLoading: isHeartRateLoading,
    error: heartRateError,
    refetch: refetchHeartRate,
  } = useOuraHeartRate(50, selectedDate);

  const { data: sessions, isLoading: isSessionsLoading } = useSessionsByDate(selectedDate);

  const {
    ppgData: sessionData,
    isLoading: isSessionDataLoading,
    error: sessionPpgError,
  } = useSessionPpg(selectedSessionId);

  const isLoading = isHeartRateLoading || isSessionDataLoading || isSessionsLoading;
  const error = heartRateError || (selectedSessionId ? sessionPpgError : null);

  const createZoomDomain = useCallback((session: any) => {
    return calculateZoomDomain(session);
  }, []);

  const handleZoomChange = useCallback((newZoomDomain: { xStart: Date; xEnd: Date } | null) => {
    setZoomDomain(newZoomDomain);
    setIsZoomed(!!newZoomDomain);
  }, []);

  const handleSelectSession = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
    setShowSessionDetails(true);
    setShowSession(true);
  }, []);

  useEffect(() => {
    if (sessionData && showSessionDetails) {
      const sessionZoomDomain = createZoomDomain(sessionData);
      handleZoomChange(sessionZoomDomain);
    }
  }, [sessionData, showSessionDetails, createZoomDomain, handleZoomChange]);

  const handleMarkerClick = useCallback(
    (sessionId: string) => {
      handleSelectSession(sessionId);
    },
    [handleSelectSession],
  );

  const handleResetZoom = useCallback(() => {
    handleZoomChange(null);
  }, [handleZoomChange]);

  const handleZoomIn = useCallback(() => {
    if (!sessionData) {
      console.warn('Cannot zoom: no session data');
      return;
    }

    const sessionZoomDomain = createZoomDomain(sessionData);
    handleZoomChange(sessionZoomDomain);
  }, [sessionData, createZoomDomain, handleZoomChange]);

  const toggleSessionData = useCallback(() => {
    setShowSession(prev => !prev);
  }, []);

  const handleCloseSessionDetails = useCallback(() => {
    setShowSessionDetails(false);
    handleResetZoom();
  }, [handleResetZoom]);

  const handleDateChange = useCallback(
    (newDate: Date) => {
      setSelectedDate(newDate);
      handleZoomChange(null);
      if (showSessionDetails) {
        setShowSessionDetails(false);
        setSelectedSessionId(undefined);
      }
    },
    [showSessionDetails, handleZoomChange],
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchHeartRate();
    } catch (error) {
      console.error('Error refreshing heart rate data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchHeartRate]);

  const headerContent = (
    <>
      <DatePicker selectedDate={selectedDate} onDateChange={handleDateChange} />
      <button
        onClick={handleRefresh}
        className="text-[var(--anl-text)] transition-colors hover:text-[var(--anl-text-muted)]"
        aria-label="Refresh"
        disabled={isRefreshing}
      >
        <motion.div
          animate={isRefreshing ? { rotate: -360 } : { rotate: 0 }}
          transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: 'linear' } : {}}
        >
          <RefreshCcw className="h-6 w-6" />
        </motion.div>
      </button>
    </>
  );

  const title =
    showSessionDetails && sessionData
      ? `Heart Rate: ${sessionData.name} Session`
      : 'Oura Ring Heart Rate Data';

  if (isLoading) {
    return (
      <BasePopup
        onClose={onClose}
        title={title}
        icon={<HeartPulse className="h-6 w-6" />}
        headerContent={headerContent}
        variant="analytics"
      >
        <LoadingState />
      </BasePopup>
    );
  }

  if (error) {
    return (
      <BasePopup
        onClose={onClose}
        title={title}
        icon={<HeartPulse className="h-6 w-6" />}
        headerContent={headerContent}
        variant="analytics"
      >
        <ErrorState error={error as Error} />
      </BasePopup>
    );
  }

  if (!heartRateData) {
    return (
      <BasePopup
        onClose={onClose}
        title={title}
        icon={<HeartPulse className="h-6 w-6" />}
        headerContent={headerContent}
        variant="analytics"
      >
        <ErrorState error={new Error('No heart rate data available')} />
      </BasePopup>
    );
  }

  return (
    <BasePopup
      onClose={onClose}
      title={title}
      icon={<HeartPulse className="h-6 w-6" />}
      headerContent={headerContent}
      variant="analytics"
    >
      <PopupContent
        heartRateData={heartRateData}
        sessions={sessions ?? []}
        sessionData={sessionData}
        showSessionDetails={showSessionDetails}
        showSession={showSession}
        isZoomed={isZoomed}
        zoomDomain={zoomDomain}
        selectedSessionId={selectedSessionId}
        onSelectSession={handleSelectSession}
        onCloseSessionDetails={handleCloseSessionDetails}
        onToggleSession={toggleSessionData}
        onZoom={handleZoomIn}
        onResetZoom={handleResetZoom}
        onMarkerClick={handleMarkerClick}
        onZoomChange={handleZoomChange}
        refetchHeartRate={refetchHeartRate}
      />
    </BasePopup>
  );
}
